<template>
  <!-- match skeleton loader width to max-width to avoid buttons shifting -->
  <v-skeleton-loader
    class="last-triggered-info"
    :width="width"
    :style="{ 'min-width': `${width}px` }"
    type="text"
    :loading="loading"
  >
    <div v-if="noRecordedRuns" class="last-run-text last-run-text--empty">
      <div class="last-run-time">
        {{ $t('gridView.engineNoRecordedRuns') }}
      </div>
    </div>
    <div v-else-if="simplified" class="last-run-text last-run-text--simplified">
      <div class="last-run-time">
        {{ formatDate(lastTriggeredInfo.date) }}
      </div>
    </div>
    <div v-else class="last-run-text">
      <div class="last-run-time">
        {{ $t(translationTimeKey) }}
        <strong>{{ formatDate(lastTriggeredInfo.date) }}</strong>
      </div>
      <div class="last-run-user">
        {{ $t('gridView.engineLastRunBy') }} <strong>{{ lastTriggeredInfo.username }}</strong>
      </div>
      <div v-if="usedSimulatedCompetitorPrices" class="last-run-sim">
        {{ $t('gridView.engineLastRunWith') }}
        <strong>{{ $t('gridView.engineLastRunSimulatedCompetitorPrices') }}</strong>
      </div>
      <div v-if="skippedAlerts" class="last-run-sim">
        <strong>{{ $t('gridView.engineLastRunWithoutAlerts') }}</strong>
      </div>
      <div v-if="hasMarginSteeringDeltaInfo" class="last-run-delta">
        {{ $t('gridView.marginInitialisationLastRunWith') }}
        <strong v-if="retainedMarginSteeringDelta">{{
          $t('gridView.marginSteeringDeltaRetained')
        }}</strong>
        <strong v-else>{{ $t('gridView.marginSteeringDeltaNotRetained') }}</strong>
      </div>
      <slot name="extrainfo" />
    </div>
  </v-skeleton-loader>
</template>

<script>
import moment from 'moment';
import { isEmpty } from 'lodash';
import { hoursMinutesDayMonthYearSeparatedFormat } from '@enums/date-formats';
import { getValueFromAuditLogByKey, isAuditLogFlagTrue } from '@/js/utils/audit-log';

export default {
  name: 'LastTriggeredInfo',

  props: {
    loading: {
      type: Boolean,
      required: true,
    },
    lastTriggeredInfo: {
      type: Object,
      required: true,
    },
    translationTimeKey: {
      type: String,
      default: 'gridView.engineLastRunTime',
    },
    source: {
      type: String,
      required: false,
    },
    width: {
      type: Number,
      default: 185,
    },
    simplified: {
      // Only display last run timestamp
      type: Boolean,
      default: false,
    },
  },

  computed: {
    noRecordedRuns() {
      return (
        (isEmpty(this.lastTriggeredInfo) || this.isWorkpackageCreatedWithoutOptimisation) &&
        !this.loading
      );
    },
    // we don't check for `enableCompetitorSimulation` feature-flag here
    // so that even if it's toggled off, we can tell if the engine ran using simulated competitor prices
    usedSimulatedCompetitorPrices() {
      return isAuditLogFlagTrue({
        auditLogEntry: this.lastTriggeredInfo,
        key: 'useSimulatedCompetitorPrices',
      });
    },

    /** True when the workpackage was created without optimisation (setIntentionPrice run). */
    isWorkpackageCreatedWithoutOptimisation() {
      return isAuditLogFlagTrue({
        auditLogEntry: this.lastTriggeredInfo,
        key: 'setIntentionPrice',
      });
    },

    skippedAlerts() {
      return isAuditLogFlagTrue({
        auditLogEntry: this.lastTriggeredInfo,
        key: 'skipAlerts',
      });
    },

    retainedMarginSteeringDelta() {
      return isAuditLogFlagTrue({
        auditLogEntry: this.lastTriggeredInfo,
        key: 'retainMarginSteeringDelta',
      });
    },
    hasMarginSteeringDeltaInfo() {
      return (
        getValueFromAuditLogByKey({
          auditLogEntry: this.lastTriggeredInfo,
          key: 'retainMarginSteeringDelta',
        }) !== undefined
      );
    },
  },

  methods: {
    formatDate(date) {
      return moment(date).format(hoursMinutesDayMonthYearSeparatedFormat);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

/* Add styling for the last triggered info */
.last-triggered-info {
  margin-right: 8px; /* Only right margin to match button spacing */
}

.last-run-text {
  color: var(--text-icon-color-text-lighter, $text-color-muted);
  font-family: 'Noto Sans';
  font-size: 11px;
  font-style: normal;
  font-weight: 400; /* Normal weight for regular text */
  line-height: 13px;

  .last-run-time,
  .last-run-user,
  .last-run-sim {
    white-space: nowrap;
  }

  strong {
    font-weight: 700; /* Bold only for strong elements */
  }
}

::v-deep .last-run-text {
  &--empty,
  &--simplified {
    font-weight: 500 !important;
  }
}
</style>
